/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="common.module.ts" />

module Meevo.Components.Common {

    /**
     * Shared bridge logic for mounting the <meevo-product-usage-table> custom
     * element in Appointment Book apptBook mode. Follows the same three-layer
     * pattern as LotModalBridgeHelper: host-bridge construction, session teardown
     * wiring, custom-element-registration check, and error path are all here so
     * the thin AppointmentBook wrapper (ProductUsageModalBridge) stays injection-free.
     */
    export class ProductUsageModalBridgeHelper {

        private static readonly PRODUCT_USAGE_BUNDLE_URL = 'lib/meevo-product-usage-table.js';
        private static readonly PRODUCT_USAGE_TAG = 'meevo-product-usage-table';

        static $inject = [
            '$rootScope',
            '$translate',
            'meevo.components.common.IdentityService',
            'meevo.AppSettings',
            'meevo.components.modal.ModalService',
            'meevo.components.common.SecurityService',
        ];

        constructor(
            private $rootScope: angular.IRootScopeService,
            private $translate: angular.translate.ITranslateService,
            private identityService: Meevo.Components.Common.IIdentityService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private modalService: Meevo.Components.Modal.IModalService,
            private securityService: Meevo.Components.Common.ISecurityService,
        ) {
            ProductUsageModalBridgeHelper.preloadElement();
        }

        /**
         * Inject the WC bundle <script> tag into <head>. Idempotent — safe to call multiple
         * times. Guarded by both a script-tag selector check and a customElements.get()
         * check so concurrent calls or repeat invocations from different code paths
         * (constructor preload + defensive call in ensureElementRegistered) never produce
         * duplicate <script> tags.
         */
        private static preloadElement(): void {
            if (document.querySelector('script[src*="meevo-product-usage-table"]')) return;
            if ((window as any).customElements && (window as any).customElements.get('meevo-product-usage-table')) return;
            const s = document.createElement('script');
            s.src = ProductUsageModalBridgeHelper.PRODUCT_USAGE_BUNDLE_URL;
            s.async = true;
            document.head.appendChild(s);
        }

        /**
         * Public API: mount <meevo-product-usage-table> in apptBook mode for the
         * given appointment service.
         */
        public openModal(opts: {
            appointmentServiceId: string;
            clientDisplayName: string;
            onSaved?: () => void;
            onCancelled?: () => void;
        }): void {
            Promise.all([
                this.securityService.checkModuleEnabledForEffectiveLocation(Meevo.Model.ModuleEnum.ProductLotExpirationTracking),
                this.securityService.checkModuleEnabledForEffectiveLocation(Meevo.Model.ModuleEnum.Charts),
                this.securityService.checkModuleEnabledForEffectiveLocation(Meevo.Model.ModuleEnum.ChartProductUsage),
            ]).then(results => {
                const lotsEnabled          = results[0];
                const chartsEnabled        = results[1];
                const chartPuModuleEnabled = results[2];
                this.ensureElementRegistered(ProductUsageModalBridgeHelper.PRODUCT_USAGE_TAG)
                    .then(() => this.mount(opts, lotsEnabled, chartsEnabled, !chartPuModuleEnabled))
                    .catch(() => this.showLoadFailedAlert());
            }).catch(() => this.showLoadFailedAlert());
        }

        private mount(opts: {
            appointmentServiceId: string;
            clientDisplayName: string;
            onSaved?: () => void;
            onCancelled?: () => void;
        }, lotsEnabled: boolean, chartsEnabled: boolean, puDisabledByModule: boolean): void {
            const el = document.createElement(ProductUsageModalBridgeHelper.PRODUCT_USAGE_TAG) as any;

            el.hostBridge         = this.buildHostBridge();
            el.apptBook           = true;
            el.chartsEnabled      = chartsEnabled;
            el.appointServices    = [{ id: opts.appointmentServiceId, name: opts.clientDisplayName, serviceId: '' }];
            el.clientChartId      = null;
            el.lotsEnabled        = lotsEnabled;
            el.puDisabledByModule = puDisabledByModule;
            el.stepName           = this.$translate.instant('put.apptBook.stepNamePrefix') + opts.clientDisplayName;

            let savedHandler: () => void;
            let cancelledHandler: () => void;
            let authExpiredHandler: () => void;

            const cleanup = () => {
                el.removeEventListener('puApptBookSaved', savedHandler);
                el.removeEventListener('puCancelled', cancelledHandler);
                el.removeEventListener('auth-expired', authExpiredHandler);
                removeSessionListeners();
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            };

            savedHandler = () => {
                cleanup();
                this.$rootScope.$applyAsync(() => {
                    if (opts.onSaved) { opts.onSaved(); }
                });
            };
            cancelledHandler = () => {
                cleanup();
                this.$rootScope.$applyAsync(() => {
                    if (opts.onCancelled) { opts.onCancelled(); }
                });
            };
            authExpiredHandler = () => {
                cleanup();
                this.$rootScope.$applyAsync(() => {
                    this.$rootScope.$emit(
                        Meevo.Components.Common.Messages.SERVER_VALIDATION,
                        this.$translate.instant('ERROR_SESSION_EXPIRED'),
                        Meevo.Model.NotificationModeType.Modal,
                        true,
                    );
                    this.identityService.clearToken();
                });
            };

            el.addEventListener('puApptBookSaved', savedHandler);
            el.addEventListener('puCancelled', cancelledHandler);
            el.addEventListener('auth-expired', authExpiredHandler);

            const removeSessionListeners = this.wireSessionCleanup(cleanup);
            document.body.appendChild(el);
        }

        /**
         * Wire Shell-driven session teardown listeners. Returns a function that
         * unsubscribes all three listeners. Call the returned function in cleanup()
         * so listeners don't outlive the mounted element.
         *
         * Without these listeners the product usage modal stays on top of the
         * Shell's lockout / login dialog on inactivity-driven logout — identical
         * rationale as LotModalBridgeHelper.
         */
        private wireSessionCleanup(cleanup: () => void): () => void {
            const unsubSessionExpiring = this.$rootScope.$on(
                Meevo.Components.Common.Messages.SESSION_EXPIRING,
                () => cleanup(),
            );
            const unsubAutoLogout = this.$rootScope.$on(
                Meevo.Components.Common.Messages.AUTO_LOGOUT_USER,
                () => cleanup(),
            );
            const unsubSessionTimeout = this.$rootScope.$on(
                Meevo.Components.Common.Messages.SESSION_TIMEOUT_HANDLER,
                () => cleanup(),
            );
            return () => {
                unsubSessionExpiring();
                unsubAutoLogout();
                unsubSessionTimeout();
            };
        }

        /**
         * Resolve when the supplied custom-element tag is registered, or reject after 5s if the
         * bundle failed to load / define it. Callers surface the rejection as a modal alert so the
         * user sees something actionable instead of a silent no-op.
         */
        private ensureElementRegistered(tag: string): Promise<void> {
            if ((window as any).customElements && (window as any).customElements.get(tag)) {
                return Promise.resolve();
            }
            // Defensive: the constructor already preloaded on injection, but call again
            // in case some future code path acquires a helper instance another way.
            // preloadElement is idempotent.
            ProductUsageModalBridgeHelper.preloadElement();
            const timeout = new Promise<void>((_resolve, reject) => {
                window.setTimeout(() => reject(new Error(tag + ' registration timed out')), 5000);
            });
            const ready = (window as any).customElements.whenDefined(tag).then(() => undefined);
            return Promise.race([ready, timeout]);
        }

        /**
         * Build the MeevoHostBridge-shaped object the web component expects.
         */
        private buildHostBridge(): any {
            return {
                initialize: () => this.buildBootstrapContext(),
                getCurrentContext: () => this.buildBootstrapContext(),
                refreshLockoutLastEventTime: () => { /* no-op — host-shell drives lockout independently */ },
            };
        }

        /**
         * Build a MeevoHostBootstrapContext from the AngularJS identity + app settings.
         */
        private buildBootstrapContext(): any {
            const identity = this.identityService.current();
            const win = window as any;
            const bearerToken = win.bearerToken || sessionStorage.getItem('mil:meevo:token') || '';
            const localeId = this.$translate.use() || (this.appSettings && this.appSettings.APP_LANGUAGEID) || '';

            return {
                apiBaseUrl: (this.appSettings && this.appSettings.API_ROOT) || '',
                session: {
                    bearerToken: bearerToken,
                    sessionId: (identity && (identity as any).sessionId) || '',
                    tenantId: identity ? identity.tenantId : null,
                    userId: identity ? identity.userId : null,
                    userName: identity ? identity.userName : null,
                    displayName: identity ? identity.displayName : null,
                    locationId: identity ? identity.locationId : null,
                    locationName: identity ? identity.locationName : null,
                    localeId: localeId,
                    isAuthenticated: !!(identity && identity.isAuthenticated),
                },
                appSettings: {
                    API_ROOT: (this.appSettings && this.appSettings.API_ROOT) || '',
                    APP_LANGUAGEID: localeId,
                    APP_COUNTRYID: (this.appSettings && this.appSettings.APP_COUNTRYID) || '',
                    APP_ISOCOUNTRYCODE: (this.appSettings && this.appSettings.APP_ISOCOUNTRYCODE) || '',
                },
                securityItems: [],
            };
        }

        /**
         * Shown when the WC bundle failed to register the custom element (network error, bundle
         * not loaded, etc.).
         */
        private showLoadFailedAlert(): void {
            this.modalService.alert(
                'ERROR_PRODUCT_USAGE_MODAL_LOAD_FAILED',
                Meevo.Components.Modal.ModalTranslationModes.Translate,
                true,
            );
        }
    }

    angular.module('meevo.components.common')
        .service('meevo.components.common.ProductUsageModalBridgeHelper', ProductUsageModalBridgeHelper);
}
